import React, { useMemo } from "react";
import { Modal } from "@tyrannosaurustech/ui";
import { ParagraphS, SubtitlesS } from "../../../components/atoms/Typography";
import { Theme } from "../../../components/atoms/theme";
import { useGetRequestSilentQuery } from "../../../redux/reduxQuery/requestApi";
import { useGetProviderSilentQuery } from "../../../redux/reduxQuery/medicalProviderApi";
import { useNavigate } from "react-router-dom";

// Fields to exclude from display
const EXCLUDED_FIELDS = [
  "id",
  "createdAt",
  "updatedAt",
  "SearchField",
  "searchField",
];

// Convert camelCase to Title Case (firstName -> First Name)
const formatFieldName = (field: string): string => {
  return field
    .replace(/([A-Z])/g, " $1") // Add space before capitals
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .trim();
};

// Check if a value looks like an ISO date string
const isISODateString = (value: any): boolean => {
  if (typeof value !== "string") return false;
  const isoPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return isoPattern.test(value);
};

// Format a value for display (handles dates, booleans, etc.)
const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "null";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (isISODateString(value)) {
    return new Date(value).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }
  return String(value);
};

interface AuditDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  metadata: Record<string, any> | null;
  message: string;
}

const MetadataSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div style={{ marginBottom: "1.5rem" }}>
    <SubtitlesS
      style={{
        marginBottom: "0.75rem",
        color: Theme.colors.neutrals_000,
        fontWeight: 600,
      }}
    >
      {title}
    </SubtitlesS>
    {children}
  </div>
);

const FieldChange = ({
  field,
  oldValue,
  newValue,
}: {
  field: string;
  oldValue: string;
  newValue: string;
}) => (
  <div
    style={{
      padding: "1rem",
      backgroundColor: Theme.colors.neutrals_400,
      borderRadius: "0.5rem",
      marginBottom: "0.5rem",
      border: `1px solid ${Theme.colors.neutrals_400}`,
    }}
  >
    <ParagraphS
      style={{
        margin: "0 0 0.5rem 0",
        fontWeight: 600,
        color: Theme.colors.neutrals_000,
      }}
    >
      {field}
    </ParagraphS>
    <div style={{ display: "flex", gap: "1rem", fontSize: "0.875rem" }}>
      <div style={{ flex: 1 }}>
        <ParagraphS
          style={{
            margin: "0 0 0.25rem 0",
            color: Theme.colors.neutrals_000,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          Old Value:
        </ParagraphS>
        <ParagraphS style={{ margin: 0, color: "#dc3545", fontWeight: 500 }}>
          {oldValue}
        </ParagraphS>
      </div>
      <div style={{ flex: 1 }}>
        <ParagraphS
          style={{
            margin: "0 0 0.25rem 0",
            color: Theme.colors.neutrals_000,
            fontSize: "0.75rem",
            fontWeight: 600,
          }}
        >
          New Value:
        </ParagraphS>
        <ParagraphS style={{ margin: 0, color: "#28a745", fontWeight: 500 }}>
          {newValue}
        </ParagraphS>
      </div>
    </div>
  </div>
);

// Component to display a request reference with firm and patient names
const RequestReference = ({ requestId }: { requestId: string }) => {
  const navigate = useNavigate();
  const { data: request, isLoading } = useGetRequestSilentQuery(requestId, {
    skip: !requestId,
  }) as { data: any; isLoading: boolean };

  const displayText = useMemo(() => {
    if (isLoading) return "Loading...";
    if (!request) return `Request: ${requestId.substring(0, 8)}...`;

    const firmName = request?.firm?.name || "Unknown Firm";
    const patientName = request?.patient?.name || "Unknown Patient";
    return `${firmName} - ${patientName}`;
  }, [request, isLoading, requestId]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/admin/requests/${requestId}`);
  };

  return (
    <a
      href={`/admin/requests/${requestId}`}
      onClick={handleClick}
      style={{
        color: Theme.colors.primaries_000 || "#6c5ce7",
        textDecoration: "underline",
        cursor: "pointer",
        fontWeight: 500,
      }}
    >
      {displayText}
    </a>
  );
};

// Component to display a provider reference with name
const ProviderReference = ({ providerId }: { providerId: string }) => {
  const navigate = useNavigate();
  const { data: provider, isLoading } = useGetProviderSilentQuery(providerId, {
    skip: !providerId,
  }) as { data: any; isLoading: boolean };

  const displayText = useMemo(() => {
    if (isLoading) return "Loading...";
    if (!provider) return `Provider: ${providerId.substring(0, 8)}...`;
    return provider.name || `Provider: ${providerId.substring(0, 8)}...`;
  }, [provider, isLoading, providerId]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`/admin/medical-providers/${providerId}`);
  };

  return (
    <a
      href={`/admin/medical-providers/${providerId}`}
      onClick={handleClick}
      style={{
        color: Theme.colors.primaries_000 || "#6c5ce7",
        textDecoration: "underline",
        cursor: "pointer",
        fontWeight: 500,
      }}
    >
      {displayText}
    </a>
  );
};

export const AuditDetailsModal: React.FC<AuditDetailsModalProps> = ({
  isOpen,
  onClose,
  metadata,
  message,
}) => {
  if (!metadata) {
    return null;
  }

  const renderMetadata = () => {
    // Handle changes object (from UPDATE events)
    if (metadata.changes && typeof metadata.changes === "object") {
      const changes = metadata.changes as Record<
        string,
        { old: any; new: any }
      >;
      // Filter out excluded fields
      const filteredChanges = Object.entries(changes).filter(
        ([field]) => !EXCLUDED_FIELDS.includes(field)
      );

      return (
        <MetadataSection title="Field Changes">
          {filteredChanges.length === 0 ? (
            <ParagraphS
              style={{ color: Theme.colors.gray_000, fontStyle: "italic" }}
            >
              No changes detected
            </ParagraphS>
          ) : (
            filteredChanges.map(([field, change]) => (
              <FieldChange
                key={field}
                field={formatFieldName(field)}
                oldValue={formatValue(change.old)}
                newValue={formatValue(change.new)}
              />
            ))
          )}
        </MetadataSection>
      );
    }

    // Handle entity objects directly (from CREATE events - e.g., provider, patient, firm, user, request, medicalRequest)
    const entityKeys = [
      "provider",
      "patient",
      "firm",
      "user",
      "request",
      "medicalRequest",
    ];
    const foundEntityKeys = entityKeys.filter(
      (key) => metadata[key] && typeof metadata[key] === "object"
    );

    // Handle multiple entities (e.g., request + medicalRequests)
    if (
      foundEntityKeys.length > 1 &&
      metadata.request &&
      metadata.medicalRequests
    ) {
      const request = metadata.request as Record<string, any>;
      const medicalRequests = Array.isArray(metadata.medicalRequests)
        ? metadata.medicalRequests
        : [];

      return (
        <>
          <MetadataSection title="Request Details">
            <div
              style={{
                padding: "1.5rem",
                backgroundColor: "#e8f5e9",
                borderRadius: "0.5rem",
                border: "1px solid #c8e6c9",
              }}
            >
              {[
                "status",
                "requestedBy",
                "requesterEmails",
                "requestNotes",
                "serviceDates",
                "submittedDate",
              ].map((key) => {
                const value = request[key];
                if (value === null || value === undefined || value === "")
                  return null;
                return (
                  <div
                    key={key}
                    style={{
                      marginBottom: "1rem",
                      paddingBottom: "1rem",
                      borderBottom: "1px solid #c8e6c9",
                    }}
                  >
                    <ParagraphS
                      style={{
                        margin: "0 0 0.5rem 0",
                        fontWeight: 600,
                        color: "#2e7d32",
                        fontSize: "0.875rem",
                        textTransform: "capitalize",
                      }}
                    >
                      {key.replace(/([A-Z])/g, " $1").trim()}:
                    </ParagraphS>
                    <ParagraphS
                      style={{
                        margin: 0,
                        color: "#1b5e20",
                        fontSize: "0.9375rem",
                      }}
                    >
                      {String(value)}
                    </ParagraphS>
                  </div>
                );
              })}
            </div>
          </MetadataSection>
          {medicalRequests.length > 0 && (
            <MetadataSection
              title={`Medical Requests (${medicalRequests.length})`}
            >
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: Theme.colors.neutrals_200 || "#f5f5f5",
                  borderRadius: "0.5rem",
                  border: `1px solid ${Theme.colors.neutrals_300 || "#e0e0e0"}`,
                }}
              >
                {medicalRequests.map((mr: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "1rem",
                      padding: "1rem",
                      backgroundColor: "#ffffff",
                      borderRadius: "0.5rem",
                      border: "1px solid #c8e6c9",
                    }}
                  >
                    <ParagraphS
                      style={{
                        margin: "0 0 0.5rem 0",
                        fontWeight: 600,
                        color: "#2e7d32",
                      }}
                    >
                      {mr.requestType || `Request ${index + 1}`}
                    </ParagraphS>
                    {mr.requestNotes && (
                      <ParagraphS
                        style={{
                          margin: "0 0 0.25rem 0",
                          color: "#1b5e20",
                          fontSize: "0.875rem",
                        }}
                      >
                        Notes: {mr.requestNotes}
                      </ParagraphS>
                    )}
                    <div
                      style={{
                        display: "flex",
                        gap: "1rem",
                        marginTop: "0.5rem",
                      }}
                    >
                      {mr.isSent !== undefined && (
                        <span
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: mr.isSent ? "#c8e6c9" : "#ffecb3",
                            borderRadius: "0.25rem",
                            fontSize: "0.75rem",
                            color: mr.isSent ? "#2e7d32" : "#f57c00",
                          }}
                        >
                          {mr.isSent ? "Sent" : "Not Sent"}
                        </span>
                      )}
                      {mr.certified !== undefined && (
                        <span
                          style={{
                            padding: "0.25rem 0.5rem",
                            backgroundColor: mr.certified
                              ? "#c8e6c9"
                              : "#ffecb3",
                            borderRadius: "0.25rem",
                            fontSize: "0.75rem",
                            color: mr.certified ? "#2e7d32" : "#f57c00",
                          }}
                        >
                          {mr.certified ? "Certified" : "Not Certified"}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </MetadataSection>
          )}
        </>
      );
    }

    // Handle single entity
    if (foundEntityKeys.length > 0) {
      const entityKey = foundEntityKeys[0];
      const entity = metadata[entityKey] as Record<string, any>;
      const entityName = entityKey.charAt(0).toUpperCase() + entityKey.slice(1);

      // Fields to prioritize (show at top)
      const priorityFields: Record<string, string[]> = {
        provider: [
          "name",
          "type",
          "status",
          "email",
          "phone",
          "address",
          "city",
          "state",
          "zip",
        ],
        patient: [
          "name",
          "dateOfBirth",
          "address",
          "city",
          "state",
          "zip",
          "phone",
          "email",
        ],
        firm: [
          "name",
          "contactName",
          "contactEmail",
          "phone",
          "address",
          "city",
          "state",
          "zip",
        ],
        user: ["name", "email", "roles", "firmId"],
        request: [
          "status",
          "requestedBy",
          "requesterEmails",
          "requestNotes",
          "serviceDates",
          "submittedDate",
        ],
        medicalRequest: ["requestType", "requestNotes", "isSent", "certified"],
      };

      const priority = priorityFields[entityKey] || [];
      const allKeys = Object.keys(entity);
      const priorityKeys = priority.filter((key) => key in entity);
      const otherKeys = allKeys.filter(
        (key) =>
          !priority.includes(key) &&
          key !== "id" &&
          !key.includes("At") &&
          !key.includes("Search")
      );

      return (
        <MetadataSection title={`${entityName} Details`}>
          <div
            style={{
              padding: "1.5rem",
              backgroundColor: "#e8f5e9",
              borderRadius: "0.5rem",
              border: "1px solid #c8e6c9",
            }}
          >
            {/* Priority fields */}
            {priorityKeys.length > 0 && (
              <div style={{ marginBottom: "1.5rem" }}>
                {priorityKeys.map((key) => {
                  const value = entity[key];
                  if (value === null || value === undefined || value === "")
                    return null;

                  return (
                    <div
                      key={key}
                      style={{
                        marginBottom: "1rem",
                        paddingBottom: "1rem",
                        borderBottom: "1px solid #c8e6c9",
                      }}
                    >
                      <ParagraphS
                        style={{
                          margin: "0 0 0.5rem 0",
                          fontWeight: 600,
                          color: "#2e7d32",
                          fontSize: "0.875rem",
                          textTransform: "capitalize",
                        }}
                      >
                        {key.replace(/([A-Z])/g, " $1").trim()}:
                      </ParagraphS>
                      <ParagraphS
                        style={{
                          margin: 0,
                          color: "#1b5e20",
                          fontSize: "0.9375rem",
                        }}
                      >
                        {typeof value === "boolean"
                          ? value
                            ? "Yes"
                            : "No"
                          : String(value)}
                      </ParagraphS>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Other fields */}
            {otherKeys.length > 0 && (
              <div>
                <ParagraphS
                  style={{
                    margin: "0 0 0.75rem 0",
                    fontWeight: 600,
                    color: "#2e7d32",
                    fontSize: "0.875rem",
                  }}
                >
                  Additional Information:
                </ParagraphS>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "0.75rem",
                  }}
                >
                  {otherKeys.map((key) => {
                    const value = entity[key];
                    if (value === null || value === undefined || value === "")
                      return null;

                    return (
                      <div
                        key={key}
                        style={{
                          padding: "0.75rem",
                          backgroundColor: "#ffffff",
                          borderRadius: "0.375rem",
                          border: "1px solid #c8e6c9",
                        }}
                      >
                        <ParagraphS
                          style={{
                            margin: "0 0 0.25rem 0",
                            fontWeight: 600,
                            color: "#2e7d32",
                            fontSize: "0.75rem",
                            textTransform: "capitalize",
                          }}
                        >
                          {key.replace(/([A-Z])/g, " $1").trim()}:
                        </ParagraphS>
                        <ParagraphS
                          style={{
                            margin: 0,
                            color: "#1b5e20",
                            fontSize: "0.8125rem",
                            wordBreak: "break-word",
                          }}
                        >
                          {typeof value === "boolean"
                            ? value
                              ? "Yes"
                              : "No"
                            : String(value).length > 50
                            ? String(value).substring(0, 50) + "..."
                            : String(value)}
                        </ParagraphS>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </MetadataSection>
      );
    }

    // Handle created object (from CREATE events using createAuditMetadata)
    if (metadata.created && typeof metadata.created === "object") {
      return (
        <MetadataSection title="Created Data">
          <div
            style={{
              padding: "1rem",
              backgroundColor: Theme.colors.neutrals_200 || "#f5f5f5",
              borderRadius: "0.5rem",
              border: `1px solid ${Theme.colors.neutrals_300 || "#e0e0e0"}`,
            }}
          >
            {Object.entries(metadata.created).map(([key, value]) => (
              <div key={key} style={{ marginBottom: "0.75rem" }}>
                <ParagraphS
                  style={{
                    margin: "0 0 0.25rem 0",
                    fontWeight: 600,
                    color: Theme.colors.neutrals_000,
                  }}
                >
                  {key}:
                </ParagraphS>
                <ParagraphS
                  style={{ margin: 0, color: Theme.colors.neutrals_000 }}
                >
                  {typeof value === "object"
                    ? JSON.stringify(value, null, 2)
                    : String(value)}
                </ParagraphS>
              </div>
            ))}
          </div>
        </MetadataSection>
      );
    }

    // Handle bulk update metadata
    if (metadata.count !== undefined || metadata.succeeded || metadata.failed) {
      return (
        <>
          {metadata.count !== undefined && (
            <MetadataSection title="Summary">
              <ParagraphS style={{ color: Theme.colors.neutrals_000 }}>
                Total items updated: <strong>{metadata.count}</strong>
              </ParagraphS>
            </MetadataSection>
          )}
          {metadata.succeeded &&
            Array.isArray(metadata.succeeded) &&
            metadata.succeeded.length > 0 && (
              <MetadataSection title="Succeeded">
                <div
                  style={{
                    padding: "1rem",
                    backgroundColor: "#d4edda",
                    borderRadius: "0.5rem",
                    border: "1px solid #c3e6cb",
                  }}
                >
                  {metadata.succeeded.map((id: string, index: number) => (
                    <div
                      key={index}
                      style={{
                        margin: "0.5rem 0",
                        padding: "0.5rem",
                        backgroundColor: "#ffffff",
                        borderRadius: "0.25rem",
                        border: "1px solid #c3e6cb",
                      }}
                    >
                      <ParagraphS
                        style={{ margin: 0, color: "#155724", fontWeight: 500 }}
                      >
                        <RequestReference requestId={id} />
                      </ParagraphS>
                    </div>
                  ))}
                </div>
              </MetadataSection>
            )}
          {metadata.failed &&
            Array.isArray(metadata.failed) &&
            metadata.failed.length > 0 && (
              <MetadataSection title="Failed">
                <div
                  style={{
                    padding: "1rem",
                    backgroundColor: "#f8d7da",
                    borderRadius: "0.5rem",
                    border: "1px solid #f5c6cb",
                  }}
                >
                  {metadata.failed.map((id: string, index: number) => (
                    <div
                      key={index}
                      style={{
                        margin: "0.5rem 0",
                        padding: "0.5rem",
                        backgroundColor: "#ffffff",
                        borderRadius: "0.25rem",
                        border: "1px solid #f5c6cb",
                      }}
                    >
                      <ParagraphS
                        style={{ margin: 0, color: "#721c24", fontWeight: 500 }}
                      >
                        <RequestReference requestId={id} />
                      </ParagraphS>
                    </div>
                  ))}
                </div>
              </MetadataSection>
            )}
          {metadata.updatedFields && Array.isArray(metadata.updatedFields) && (
            <MetadataSection title="Updated Fields">
              <div
                style={{
                  padding: "1rem",
                  backgroundColor: Theme.colors.neutrals_200 || "#f5f5f5",
                  borderRadius: "0.5rem",
                  border: `1px solid ${Theme.colors.neutrals_300 || "#e0e0e0"}`,
                }}
              >
                <div
                  style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}
                >
                  {metadata.updatedFields.map(
                    (field: string, index: number) => (
                      <span
                        key={index}
                        style={{
                          padding: "0.375rem 0.75rem",
                          backgroundColor:
                            Theme.colors.primaries_000 || "#6c5ce7",
                          color: "#ffffff",
                          borderRadius: "0.25rem",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        }}
                      >
                        {field}
                      </span>
                    )
                  )}
                </div>
              </div>
            </MetadataSection>
          )}
        </>
      );
    }

    // Handle status change metadata
    if (metadata.oldStatus !== undefined || metadata.newStatus !== undefined) {
      return (
        <MetadataSection title="Status Change">
          <FieldChange
            field="Status"
            oldValue={formatValue(metadata.oldStatus)}
            newValue={formatValue(metadata.newStatus)}
          />
        </MetadataSection>
      );
    }

    // Handle connection metadata (Provider Connections)
    if (metadata.fromId || metadata.toId) {
      return (
        <MetadataSection title="Connection Details">
          <div
            style={{
              padding: "1rem",
              backgroundColor: Theme.colors.neutrals_200 || "#f5f5f5",
              borderRadius: "0.5rem",
              border: `1px solid ${Theme.colors.neutrals_300 || "#e0e0e0"}`,
            }}
          >
            {metadata.fromId && (
              <div style={{ marginBottom: "0.75rem" }}>
                <ParagraphS
                  style={{
                    margin: "0 0 0.25rem 0",
                    fontWeight: 600,
                    color: Theme.colors.neutrals_000,
                  }}
                >
                  From Provider:
                </ParagraphS>
                <ParagraphS
                  style={{ margin: 0, color: Theme.colors.neutrals_000 }}
                >
                  <ProviderReference providerId={metadata.fromId} />
                </ParagraphS>
              </div>
            )}
            {metadata.toId && (
              <div>
                <ParagraphS
                  style={{
                    margin: "0 0 0.25rem 0",
                    fontWeight: 600,
                    color: Theme.colors.neutrals_000,
                  }}
                >
                  To Provider:
                </ParagraphS>
                <ParagraphS
                  style={{ margin: 0, color: Theme.colors.neutrals_000 }}
                >
                  <ProviderReference providerId={metadata.toId} />
                </ParagraphS>
              </div>
            )}
          </div>
        </MetadataSection>
      );
    }

    // Fallback: show all metadata as key-value pairs
    return (
      <MetadataSection title="Details">
        <div
          style={{
            padding: "1rem",
            backgroundColor: Theme.colors.neutrals_200 || "#f5f5f5",
            borderRadius: "0.5rem",
            border: `1px solid ${Theme.colors.neutrals_300 || "#e0e0e0"}`,
          }}
        >
          {Object.entries(metadata).map(([key, value]) => (
            <div key={key} style={{ marginBottom: "0.75rem" }}>
              <ParagraphS
                style={{
                  margin: "0 0 0.25rem 0",
                  fontWeight: 600,
                  color: Theme.colors.neutrals_000,
                }}
              >
                {key}:
              </ParagraphS>
              <ParagraphS
                style={{ margin: 0, color: Theme.colors.neutrals_000 }}
              >
                {typeof value === "object"
                  ? JSON.stringify(value, null, 2)
                  : String(value)}
              </ParagraphS>
            </div>
          ))}
        </div>
      </MetadataSection>
    );
  };

  return (
    <Modal
      open={isOpen}
      setOpen={onClose}
      title="Audit Log Details"
      onConfirm={onClose}
      confirmText="Close"
      noCancel={true}
      panelClassName="!max-w-[800px]"
    >
      <div style={{ padding: "1rem 0" }}>
        <MetadataSection title="Message">
          <ParagraphS style={{ color: Theme.colors.neutrals_000 }}>
            {message}
          </ParagraphS>
        </MetadataSection>
        {renderMetadata()}
      </div>
    </Modal>
  );
};
